// v2.15.2 — Modale Domicile via géoloc (1-click GPS).
// v2 premium (2026-06) — Refonte visuelle alignée sur l'identité V2 (vert profond + or)
// + phase succès confirmant l'enregistrement dans le profil.
// Flow :
//   1. À l'ouverture, navigator.geolocation.getCurrentPosition (fallback Plateau si refus/hors-CI)
//   2. POST /api/v3/addresses/from-geoloc → preview (address_civ, commune, suffix)
//   3. User confirme nom (pré-rempli "Domicile") → POST /api/v3/addresses → home créé
// Pas de selects ville/commune visibles. Pas de clic carte. Pas de modif suffix.

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import MiniMapPreview from "@/components/MiniMapPreview";
import "./DomicileGeolocModal.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const PLATEAU_FALLBACK = { lat: 5.3266, lng: -4.0173 };
const CI_BBOX = { latMin: 4.0, latMax: 11.0, lngMin: -9.0, lngMax: -2.0 };
function isInCI(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng)
    && lat >= CI_BBOX.latMin && lat <= CI_BBOX.latMax
    && lng >= CI_BBOX.lngMin && lng <= CI_BBOX.lngMax;
}

export default function DomicileGeolocModal({ open, userId, onClose, onCreated }) {
  const [phase, setPhase] = useState("locating"); // locating | confirm | submitting | error | done
  const [outOfCI, setOutOfCI] = useState(false);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("Domicile");
  const [description, setDescription] = useState("");
  const [errorMsg, setErrorMsg] = useState(null);
  const [created, setCreated] = useState(null); // adresse créée (phase done)
  // v2.15.3 — Slug availability live.
  const [aliasStatus, setAliasStatus] = useState(null); // {available, slug, suggestions}
  const [aliasChecking, setAliasChecking] = useState(false);
  const aliasDebounceRef = useRef(null);

  // v2.15.3 — Debounce 500ms sur le nom → GET availability.
  useEffect(() => {
    if (phase !== "confirm") return;
    if (!name || !name.trim()) { setAliasStatus(null); return; }
    if (aliasDebounceRef.current) clearTimeout(aliasDebounceRef.current);
    aliasDebounceRef.current = setTimeout(async () => {
      setAliasChecking(true);
      try {
        const r = await axios.get(`${API}/v3/domicile-alias-availability`, { params: { name } });
        if (r.data?.ok) setAliasStatus(r.data);
      } catch (_e) { setAliasStatus(null); }
      finally { setAliasChecking(false); }
    }, 500);
    return () => { if (aliasDebounceRef.current) clearTimeout(aliasDebounceRef.current); };
  }, [name, phase]);

  useEffect(() => {
    if (!open) return;
    setPhase("locating");
    setOutOfCI(false);
    setPreview(null);
    setName("Domicile");
    setDescription("");
    setErrorMsg(null);
    setCreated(null);

    const handleCoords = async (lat, lng, fellback) => {
      try {
        // 2026-07-02 — token joint : permet l'exception de test gouvernée (testeurs hors CI).
        const _tk = localStorage.getItem("civ_user_token");
        const r = await axios.post(`${API}/v3/addresses/from-geoloc`, { user_id: userId, lat, lng }, {
          headers: _tk ? { Authorization: `Bearer ${_tk}` } : {},
        });
        if (r.data?.ok) {
          setPreview(r.data.preview);
          setOutOfCI(fellback);
          setPhase("confirm");
        } else {
          throw new Error(r.data?.reason || "from_geoloc_failed");
        }
      } catch (e) {
        // Fallback Plateau si la résolution échoue (typiquement out_of_zone_ci).
        if (!fellback) {
          await handleCoords(PLATEAU_FALLBACK.lat, PLATEAU_FALLBACK.lng, true);
        } else {
          setErrorMsg(e.response?.data?.reason || e.message);
          setPhase("error");
        }
      }
    };

    if (!navigator.geolocation) {
      handleCoords(PLATEAU_FALLBACK.lat, PLATEAU_FALLBACK.lng, true);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const lat = p.coords.latitude, lng = p.coords.longitude;
        if (isInCI(lat, lng)) handleCoords(lat, lng, false);
        else handleCoords(PLATEAU_FALLBACK.lat, PLATEAU_FALLBACK.lng, true);
      },
      () => handleCoords(PLATEAU_FALLBACK.lat, PLATEAU_FALLBACK.lng, true),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 1000 },
    );
  }, [open, userId]);

  const confirm = async () => {
    if (!preview || !name.trim()) { toast.error("Donne un nom à ton Domicile"); return; }
    setPhase("submitting");
    try {
      const r = await axios.post(`${API}/v3/addresses`, {
        user_id: userId,
        city_code: preview.city_code,
        commune_code: preview.commune_code,
        suffix: preview.suffix,
        name: name.trim(),
        description: description.trim() || null,
        lat: preview.lat,
        lng: preview.lng,
        is_home: true,
      });
      if (r.data?.ok) {
        toast.success(`✅ Domicile créé : ${r.data.address.address_civ}`);
        setCreated(r.data.address);
        setPhase("done");
      } else {
        throw new Error(r.data?.reason || "create_failed");
      }
    } catch (e) {
      const reason = e.response?.data?.reason || e.message;
      if (reason === "name_taken") {
        toast.error("Ce nom est déjà utilisé — essaie autre chose (ex: Mon Chez-moi)");
      } else {
        toast.error(reason);
      }
      setPhase("confirm");
    }
  };

  if (!open) return null;

  const confirmDisabled = phase === "submitting" || !name.trim() || aliasStatus?.available === false;

  return (
    <div data-testid="domicile-geoloc-modal" className="dgm-overlay">
      <div className="dgm-modal">
        <button onClick={onClose} data-testid="domicile-close-btn" className="dgm-close" aria-label="Fermer">✕</button>

        {phase === "done" && created ? (
          <div data-testid="domicile-success" className="dgm-success">
            <div className="dgm-success-ring"><span style={{ color: "#F2C66B", fontWeight: 900, fontSize: 40, lineHeight: 1 }}>✓</span></div>
            <h3 className="dgm-success-title">« <span className="dgm-success-name">{created.name || name}</span> » est créée !</h3>
            <div className="dgm-code" data-testid="domicile-success-code">
              <span className="dgm-code-label">Adresse AYA</span>
              <span className="dgm-code-value">{created.address_civ}</span>
            </div>
            <p className="dgm-success-sub">
              Ton Domicile est <strong>enregistré dans ton profil</strong> comme adresse principale.
              Livreurs, proches et services peuvent maintenant te trouver avec ce code.
            </p>
            <div className="dgm-actions">
              <button
                onClick={() => onCreated?.(created)}
                data-testid="domicile-success-view-profile"
                className="dgm-btn dgm-btn-primary"
                style={{ flex: 1 }}
              >✨ Voir dans mon profil →</button>
            </div>
          </div>
        ) : (
          <>
            <div className="dgm-eyebrow">Adresse officielle AYA</div>
            <h3 className="dgm-title">📍 Crée ton <span className="dgm-aya">Domicile</span></h3>

            {phase === "locating" && (
              <div data-testid="domicile-locating" className="dgm-locating">
                <div className="dgm-radar">
                  <span className="dgm-radar-ring" />
                  <span className="dgm-radar-ring" />
                  <span className="dgm-radar-ring" />
                  <span className="dgm-radar-pin">📡</span>
                </div>
                <div className="dgm-locating-txt">Localisation en cours…</div>
                <div className="dgm-locating-sub">AYA détecte ta position exacte</div>
              </div>
            )}

            {phase === "error" && (
              <div data-testid="domicile-error" className="dgm-error">❌ Erreur : {errorMsg}</div>
            )}

            {phase !== "locating" && phase !== "error" && preview && (
              <>
                {outOfCI && (
                  <div data-testid="domicile-outofci-banner" className="dgm-banner">
                    📡 Hors-CI détecté — utilisation du Plateau-Abidjan par défaut. Tu pourras modifier plus tard.
                  </div>
                )}
                <div className="dgm-pos">
                  📍 Position détectée : <strong>{preview.commune_name}, {preview.city_name}</strong>
                </div>
                <div data-testid="domicile-preview-code" className="dgm-code">
                  <span className="dgm-code-label">Code attribué</span>
                  <span className="dgm-code-value">{preview.address_civ}</span>
                </div>
                <div className="dgm-map">
                  <MiniMapPreview lat={preview.lat} lng={preview.lng} onSet={() => {/* read-only */}} height={160} />
                </div>

                <label className="dgm-label">Nom * <span className="dgm-label-sub">(unique en CI)</span></label>
                <div className="dgm-input-wrap">
                  <input
                    type="text"
                    data-testid="domicile-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Maison Koffi-kan (doit être unique dans toute la CI)"
                    className="dgm-input"
                    style={{ paddingRight: 86 }}
                  />
                  {aliasChecking && (
                    <span data-testid="alias-status-checking" className="dgm-badge dgm-badge-wait">⏳</span>
                  )}
                  {!aliasChecking && aliasStatus?.available && (
                    <span data-testid="alias-status-available" className="dgm-badge dgm-badge-ok">✓ Dispo</span>
                  )}
                  {!aliasChecking && aliasStatus?.available === false && (
                    <span data-testid="alias-status-taken" className="dgm-badge dgm-badge-ko">✗ Pris</span>
                  )}
                </div>
                <div className="dgm-hint">💡 Ce nom permettra à un livreur de te trouver depuis le chat.</div>
                {!aliasChecking && aliasStatus && aliasStatus.available === false && (aliasStatus.suggestions || []).length > 0 && (
                  <div data-testid="alias-suggestions" className="dgm-suggestions">
                    {aliasStatus.suggestions.slice(0, 3).map((s) => (
                      <button
                        key={s.slug}
                        type="button"
                        data-testid={`alias-suggestion-${s.slug}`}
                        onClick={() => setName(s.name)}
                        className="dgm-chip"
                      >+ {s.name}</button>
                    ))}
                  </div>
                )}

                <label className="dgm-label">Description <span className="dgm-label-sub">(optionnel)</span></label>
                <input
                  type="text"
                  data-testid="domicile-desc-input"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Ex: Immeuble bleu, 2e étage"
                  className="dgm-input"
                />

                <div className="dgm-actions">
                  <button onClick={onClose} data-testid="domicile-cancel-btn" className="dgm-btn dgm-btn-secondary">Annuler</button>
                  <button
                    onClick={confirm}
                    data-testid="domicile-confirm-btn"
                    disabled={confirmDisabled}
                    className="dgm-btn dgm-btn-primary"
                  >
                    {phase === "submitting" ? "…" : "✅ Confirmer mon Domicile"}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
